import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    Divider,
    FormControlLabel,
    FormGroup,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import MainDrawer from "../../MainDrawer";
import { useNavigate } from "react-router-dom";
import { ExpandMore } from "@mui/icons-material";
import APIGatewayTable from "../apis-gateway/APIGatewayTable"; // Changed import
import MiniBot from "../../MiniBot";
import queryGraphql from "../../../helpers/ApiInterface"; // Import the API helper

// Define the structure for our component's data (what the table/search expects)
interface Gateway {
    id: string;
    name: string;
    apiType: string; // Changed from enum to string to accept API data
    endpointType: string; // Changed from enum to string
    userPoolName: string;
    region: string;
    wafEnabled: boolean;
    tokenHeaderSource: string;
    status: string; // Changed from enum to string
    solutionOwner: string;
    createdAt: string;
    googleProfilePicture?: string;
}

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: keyof Gateway;
    groupBy: string;
}

// Hardcoded data for API Gateways REMOVED

let searchOptions: SearchOptions[] = [];

const APIGatewayPage: React.FC = () => {
    const [gateways, setGateways] = useState<Gateway[]>([]); // Initial state is an empty array
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [isLoading, setIsLoading] = useState(true);

     const [addGatewayMenuAnchorEl, setAddGatewayMenuAnchorEl] = useState<null | HTMLElement>(null);
    const isAddGatewayMenuOpen = Boolean(addGatewayMenuAnchorEl);

     const [menuStep, setMenuStep] = useState(1);

    // ----> ADD THIS NEW STATE for the SUBMENU ("Internal" / "External")
    const [apiTypeMenuAnchorEl, setApiTypeMenuAnchorEl] = useState<null | HTMLElement>(null);
    const isApiTypeMenuOpen = Boolean(apiTypeMenuAnchorEl);

    const handleAddGatewayMenuClick = (event: React.MouseEvent<HTMLElement>) => {
      setAddGatewayMenuAnchorEl(event.currentTarget);
      setMenuStep(1); // Ensures the menu always starts at the first step
    };
 
    const handleAddGatewayMenuClose = () => {
        setAddGatewayMenuAnchorEl(null);
        // We add a small delay so the content doesn't disappear before the menu animates closed
        setTimeout(() => {
            setMenuStep(1);
        }, 200);
    };

    const handleApiTypeMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setApiTypeMenuAnchorEl(event.currentTarget);
    };
    const handleApiTypeMenuClose = () => {
        setApiTypeMenuAnchorEl(null);
    };

    const handleMenuItemClick = (path: string) => {
        navigate(path);
        handleAddGatewayMenuClose();
    };

    const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>({
        name: true,
        apiType: true,
        endpointType: true,
        region: true,
        solutionOwner: true,
        createdAt: true,
        wafEnabled: true, // New field
    });
    
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleFilterClose = () => setAnchorEl(null);

    const toggleColumnFilter = (column: string) => {
        setColumnFilters((prevFilters) => ({
            ...prevFilters,
            [column]: !prevFilters[column],
        }));
    };

    // Replace the hardcoded data processing with a real data fetch
    React.useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const data = await queryGraphql(`
                    query GetAPIGateways {
                      getAPIGateways {
                        id
                        name
                        apiGatewayType
                        apiEndpointType
                        region
                        authorizerType
                        userPoolName
                        tokenHeaderSource
                     
                        apiGatewayCategory
                        awsWafEnabled
                        botEnabled
                        status
                        createdAt
                        updatedAt
                        reasonForRejection
                        apiGatewayOwner {
                          id
                          name
                          googleProfilePicture
                        }
                          apiGatewayApprover {
                            id
                            name
                            googleProfilePicture
                        }
                      }
                    }
                `);
                
                const responseData = data.getAPIGateways || [];

                // Transform the API response to match the component's expected 'Gateway' interface
                const mappedGateways: Gateway[] = responseData.map((gw: any) => ({
                    id: gw.id,
                    name: gw.name,
                    apiType: gw.apiGatewayType,           // Map API field to component field
                    endpointType: gw.apiEndpointType,     // Map API field to component field
                    userPoolName: gw.userPoolName,
                    region: gw.region,
                    wafEnabled: gw.awsWafEnabled,         // Map API field to component field
                    tokenHeaderSource: gw.tokenHeaderSource,
                    status: gw.status.toLowerCase(),      // Ensure status is lowercase
                    solutionOwner: gw.apiGatewayOwner?.name || 'N/A', // Map nested owner name
                    createdAt: new Date(Number(gw.createdAt)).toISOString(), // Format timestamp
                    googleProfilePicture: gw.apiGatewayOwner?.googleProfilePicture
                }));

                setGateways(mappedGateways); // Set the fetched and transformed data

                // Now, build the search options based on the *fetched* data
                searchOptions = mappedGateways
                    .map((gw: Gateway) => [
                        { category: "name", groupBy: "GATEWAY NAME", chipLabel: `Name: ${gw.name}`, dropdownLabel: gw.name },
                        { category: "apiType", groupBy: "API TYPE", chipLabel: `Type: ${gw.apiType}`, dropdownLabel: gw.apiType },
                        { category: "endpointType", groupBy: "ENDPOINT TYPE", chipLabel: `Endpoint: ${gw.endpointType}`, dropdownLabel: gw.endpointType },
                        { category: "region", groupBy: "REGION", chipLabel: `Region: ${gw.region}`, dropdownLabel: gw.region },
                        { category: "solutionOwner", groupBy: "OWNER", chipLabel: `Owner: ${gw.solutionOwner}`, dropdownLabel: gw.solutionOwner },
                    ])
                    .flat() as SearchOptions[];

                // Remove duplicates and sort
                searchOptions = searchOptions.filter(
                    (obj, index, self) => index === self.findIndex((t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel)
                );
                searchOptions.sort((a, b) => {
                    if (a.groupBy > b.groupBy) return 1;
                    if (a.groupBy < b.groupBy) return -1;
                    if (a.dropdownLabel > b.dropdownLabel) return 1;
                    if (a.dropdownLabel < b.dropdownLabel) return -1;
                    return 0;
                });

            } catch (error) {
                console.error("Error fetching API gateways:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []); // Run only once on component mount

    const filteredGateways =
        searchQuery.length === 0
            ? gateways
            : gateways.filter((gw: Gateway) =>
                Object.entries(gw).every(([category, value]) => {
                    const filters = searchQuery.filter((e) => e.category === category);
                    if (filters.length === 0) return true;
                    // Ensure value is a string for comparison
                    const stringValue = String(value).toLowerCase();
                    return filters.map((e) => e.dropdownLabel.toLowerCase()).includes(stringValue);
                })
            );

    const totalGatewaysOverall = gateways.length;
    const totalMatchingSearch = filteredGateways.length;
    const filteredActiveGateways = filteredGateways.filter((gw) => gw.status === "active");
    const filteredPendingGateways = filteredGateways.filter((gw) => gw.status === "pending");
    const filteredRejectedGateways = filteredGateways.filter((gw) => gw.status === "rejected");

    return (
        <Box sx={{ display: "flex" }}>
            <MiniBot />
            <MainDrawer index={"apis-gateway"} /> {/* Assuming a new index */}
            <Box component="main" sx={{ flex: 1, bgcolor: "#fafafa", minHeight: window.innerHeight }}>
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 1,
                                mx: 2,
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
                                API Gateways
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: "black",
                                        color: "white",
                                        padding: "8px 24px",
                                        fontSize: "0.875rem",
                                        textTransform: "none",
                                        '&:hover': { backgroundColor: '#333' }
                                    }}
                                    onClick={handleAddGatewayMenuClick} // Navigate to a new add page
                                >
                                    <Typography fontSize="14px" fontFamily="Gilroy-Bold">
                                        Add New Gateway
                                    </Typography>
                                </Button>
                                {/* --- Main Menu --- */}
                            <Menu
                                id="add-gateway-menu"
                                MenuListProps={{ 'aria-labelledby': 'add-gateway-button' }}
                                anchorEl={addGatewayMenuAnchorEl}
                                open={isAddGatewayMenuOpen}
                                onClose={handleAddGatewayMenuClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                {/* Step 1: Shows the initial options */}
                                {menuStep === 1 && (
                                    <div>
                                        <MenuItem onClick={() => setMenuStep(2)}>
                                            Add new API Gateway
                                        </MenuItem>
                                        <MenuItem onClick={() => handleMenuItemClick('/apis-gateway/add-to-existing')}>
                                            Add to existing API Gateway
                                        </MenuItem>
                                    </div>
                                )}

                                {/* Step 2: Shows options for a NEW Gateway */}
                                {menuStep === 2 && (
                                    <div>
                                        {/* A "Back" button to improve UX */}
                                        <Divider sx={{ my: 0.5 }} />
                                        <MenuItem onClick={() => handleMenuItemClick('/apis-gateway/add/internal')}>
                                            Internal API Gateway
                                        </MenuItem>
                                        <MenuItem onClick={() => handleMenuItemClick('/apis-gateway/add/external')}>
                                            External API Gateway
                                        </MenuItem>
                                    </div>
                                )}
                            </Menu>
                                <IconButton
                                    aria-label="Help"
                                    onClick={() => window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs`, "_blank")}
                                    sx={{ padding: 0, border: "none", ml: 2, "&:hover": { backgroundColor: "action.hover" } }}
                                >
                                    <Box component="img" src="/assets/Help.svg" alt="" sx={{ width: 40, height: 40, display: "block" }} />
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                            <Autocomplete
                                multiple
                                options={searchOptions}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
                                    ))
                                }
                                onChange={(e, newVal) => setSearchQuery(newVal as SearchOptions[])}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by gateway name, type, region etc"
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px", paddingLeft: "4px" }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "45px", width: "500px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                            />
                            <Button
                                sx={{ marginLeft: "16px", border: "1px solid #bbb", borderRadius: "10px", height: "45px", width: "45px" }}
                                onClick={handleFilterClick}
                            >
                                <img src="/assets/pause.svg" alt="Filter Icon" style={{ width: "16px" }} />
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleFilterClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            >
                                <Box sx={{ padding: "8px" }}>
                                    <Typography sx={{ fontWeight: "bold", marginBottom: "8px" }}>
                                        TABLE COLUMNS
                                    </Typography>
                                    <FormGroup>
                                        {Object.keys(columnFilters).map((column) => (
                                            <FormControlLabel
                                                key={column}
                                                control={
                                                    <Checkbox
                                                        checked={columnFilters[column as keyof typeof columnFilters]}
                                                        onChange={() => toggleColumnFilter(column)}
                                                        sx={{ color: "#000", "&.Mui-checked": { color: "#000" }, "& .MuiSvgIcon-root": { borderRadius: "50%" } }}
                                                    />
                                                }
                                                label={column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, " $1").trim()}
                                            />
                                        ))}
                                    </FormGroup>
                                    <Button variant="contained" onClick={handleFilterClose} sx={{ marginTop: "8px", backgroundColor: "black", color: "white", width: "100%" }}>
                                        Apply
                                    </Button>
                                </Box>
                            </Menu>
                        </Box>
                    </Box>
                    <Box m={2}>
                       {totalGatewaysOverall > 0 && totalMatchingSearch > 0 && !isLoading && (
                            <>
                                {filteredPendingGateways.length > 0 && (
                                    <Accordion defaultExpanded elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
                                                <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                                    Pending Gateways ({filteredPendingGateways.length})
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <APIGatewayTable gateways={filteredPendingGateways} columnFilters={columnFilters} type="pending" />
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                {filteredActiveGateways.length > 0 && (
                                    <Accordion defaultExpanded elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
                                                <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                                    Active Gateways ({filteredActiveGateways.length})
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <APIGatewayTable gateways={filteredActiveGateways} columnFilters={columnFilters} type="active" />
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                {filteredRejectedGateways.length > 0 && (
                                    <Accordion elevation={0} sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                                <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                                    Rejected Gateways ({filteredRejectedGateways.length})
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <APIGatewayTable gateways={filteredRejectedGateways} columnFilters={columnFilters} type="rejected" />
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                            </>
                        )}

                        {/* No search results message */}
                        {totalGatewaysOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor: "#FFFFFF", borderRadius: "8px" }}>
                                <img src="/assets/inbox-icon.svg" alt="No Gateways Match" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No Gateways match your search
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
                                    Try adjusting your search terms or filters.
                                </Typography>
                            </Box>
                        )}
                        
                        {/* No gateways at all message */}
                        {totalGatewaysOverall === 0 && !isLoading && (
                             <Box
                                display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                                sx={{
                                    textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
                                    backgroundColor: '#FFFFFF', borderRadius: "8px",
                                    boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
                                }}
                            >
                                <img src="/assets/inbox-icon.svg" alt="No APIs" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No API Gateways are added yet
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
                                    Click the button above to add your first API Gateway.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default APIGatewayPage;